import { Component, OnInit, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location } from '@angular/common';
import { supabase } from '../../supabase/supabase.client';
import { PropertyService, Property, PropertyImage } from '../../services/property.service';
import { FavoritesService } from '../../services/favorites.service';
import { PropertyDetailSkeletonComponent } from './property-detail-skeleton.component';
import { SafeHtmlPipe } from '../../shared/pipes/safe-html.pipe';

@Component({
  selector: 'app-property-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, PropertyDetailSkeletonComponent, SafeHtmlPipe],
  templateUrl: './property-detail.component.html',
  styleUrl: './property-detail.component.css'
})
export class PropertyDetailComponent implements OnInit {
  property = signal<Property | null>(null);
  loading = signal(true);
  error = signal('');
  selectedImageIndex = signal(0);
  images = signal<PropertyImage[]>([]);
  currentImageUrl = signal('');
  imageUrls = signal<Map<string, string>>(new Map()); // Store all loaded image URLs
  isLightboxOpen = signal(false);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    public propertyService: PropertyService,
    public favoritesService: FavoritesService
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.loadProperty(id);
      }
    });
  }

  private async loadProperty(id: string) {
    try {
      this.loading.set(true);
      this.error.set('');

      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          property_images (
            id,
            property_id,
            storage_path,
            alt_text,
            is_primary,
            position
          )
        `)
        .eq('id', id)
        .eq('is_published', true)
        .single();

      if (error) {
        this.error.set('Property not found');
        return;
      }

      if (!data) {
        this.error.set('Property not found');
        return;
      }

      this.property.set(data);

      // Load images
      if (data.property_images && data.property_images.length > 0) {
        const sortedImages = [...data.property_images].sort((a, b) => a.position - b.position);
        this.images.set(sortedImages);
        
        // Preload all image URLs and store them locally
        const urls = new Map<string, string>();
        for (const img of sortedImages) {
          const url = await this.propertyService.getImageUrl(img.storage_path);
          if (url) {
            urls.set(img.storage_path, url);
          }
        }
        this.imageUrls.set(urls);
        
        await this.loadImageUrl(0);
      }
    } catch (err: any) {
      this.error.set(err.message || 'Failed to load property');
    } finally {
      this.loading.set(false);
    }
  }

  private async loadImageUrl(index: number) {
    const images = this.images();
    if (index >= 0 && index < images.length) {
      const imageData = images[index];
      const url = await this.propertyService.getImageUrl(imageData.storage_path);
      this.currentImageUrl.set(url);
      this.selectedImageIndex.set(index);
    }
  }

  selectImage(index: number) {
    this.loadImageUrl(index);
  }

  nextImage() {
    const currentIndex = this.selectedImageIndex();
    const imagesLength = this.images().length;
    const nextIndex = (currentIndex + 1) % imagesLength;
    this.selectImage(nextIndex);
  }

  previousImage() {
    const currentIndex = this.selectedImageIndex();
    const imagesLength = this.images().length;
    const prevIndex = (currentIndex - 1 + imagesLength) % imagesLength;
    this.selectImage(prevIndex);
  }

  getThumbnailUrl(storagePath: string): string {
    return this.imageUrls().get(storagePath) || '';
  }

  goBack() {
    this.location.back();
  }

  openLightbox() {
    this.isLightboxOpen.set(true);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.isLightboxOpen.set(false);
    // Restore body scroll
    document.body.style.overflow = 'auto';
  }

  previousImageLightbox() {
    this.previousImage();
  }

  nextImageLightbox() {
    this.nextImage();
  }

  toggleFavorite() {
    const prop = this.property();
    if (prop) {
      this.favoritesService.toggleFavorite(prop.id);
    }
  }

  isFavorite(): boolean {
    const prop = this.property();
    return prop ? this.favoritesService.isFavorite(prop.id) : false;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.isLightboxOpen()) {
      switch(event.key) {
        case 'Escape':
          this.closeLightbox();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.previousImage();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.nextImage();
          break;
      }
    }
  }
}
